import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { calcGridPositionsForVerticalMonth, getLocalDate } from 'lib/datetime/calendarUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import OfferCalendarSidebarDayCell from './OfferCalendarSidebarDayCell'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import {
  BundledOfferState,
  OfferPageActions,
  takeCurrentOfferState,
} from 'contexts/OfferPage/offerPageStateReducer'
import { getCalendarCheapestDays, getComplementaryBundleState } from 'selectors/offerPage/offerPageSelectors'
import useBundleAndSaveCalendarDayPickingUtils from 'hooks/Offers/BundleAndSave/useBundleAndSaveCalendarDayPickingUtils'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isVillaOffer } from 'lib/offer/offerTypes'

const Grid = styled.div`
  display: grid;
  border: 1px solid ${props => props.theme.palette.neutral.five};
  border-bottom: 0;
  position: relative;
  grid-template-columns: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(7 , 1fr);
    border: none;
  }
`

const Root = styled.div`
  width: 100%;
`

interface Props {
  days: Array<App.CalendarDay>;
  tacticalDays: Array<string>;
  enableCalendarRecommendations: boolean;
  selectedDay?: App.CalendarDay;
  className?: string;
  bundleOfferId?: string;
  currentOfferId?: string;
  duration: number;
  complementaryBundleState?: BundledOfferState
  cacheActive?: boolean;
  otherAvailability: { [checkIn: string]: boolean };
  isRoomSelected?: boolean;
  flights?: boolean;
  flightsOnly?: boolean;
  cheapestDays: App.CalendarCheapestDay[];
  offer: App.Offer;
  displayMemberPrice: boolean;
}

function OfferCalendarSidebarDaysMonth(props: Props) {
  const {
    days,
    tacticalDays,
    className,
    duration,
    enableCalendarRecommendations,
    otherAvailability,
    selectedDay,
    isRoomSelected,
    flights,
    flightsOnly,
    bundleOfferId,
    currentOfferId,
    complementaryBundleState,
    cheapestDays,
    offer,
    displayMemberPrice,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const firstDate = days[0]

  const selectedRange = useMemo(() => {
    if (selectedDay) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(selectedDay.checkIn),
        getLocalDate(selectedDay.checkOut),
      ).range
    }
    return undefined
  }, [selectedDay, firstDate])

  const {
    isComplementaryBundleCell,
    isComplementaryCheckInCell,
    isComplementaryCheckOutCell,
    isIllegalBundleSelection,
  } = useBundleAndSaveCalendarDayPickingUtils(complementaryBundleState)

  const onDayClick = useCallback((day: App.CalendarDay) => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, moment(day.checkIn).format(ISO_DATE_FORMAT))
  }, [onUserAnalyticsAction])

  const dispatch = useOfferPageDispatch()
  const onInvalidBundleDateClick = useCallback(
    () => {
      showSnackbar(
        'The dates for Hotel 2 must come after Hotel 1. Try swapping the order of the hotels.', 'warning',
        {
          action: {
            label: 'Swap order',
            onAction: () => {
              dispatch({ type: OfferPageActions.openSwapModal })
            },
          },
        },
      )
    },
    [dispatch],
  )

  return (
    <Root className={className}>
      <Grid>
        {days.map((day, index) => <OfferCalendarSidebarDayCell
          key={day.checkIn}
          enableCalendarRecommendations={enableCalendarRecommendations}
          duration={duration}
          bundleOfferId={bundleOfferId}
          day={day}
          hasOtherAvailability={otherAvailability[day.checkIn] && !currentOfferId}
          isCheckIn={day.checkIn === selectedDay?.checkIn}
          isCheckOut={day.checkIn === selectedDay?.checkOut}
          isBetweenDay={!!(selectedRange && (index + 1) >= selectedRange.start && index < (selectedRange.end - 1))}
          isRoomSelected={isRoomSelected}
          flightsOnly={flightsOnly}
          flights={flights}
          currentOfferId={currentOfferId}
          isComplementaryBundleCell={isComplementaryBundleCell(day)}
          isComplementaryCheckIn={isComplementaryCheckInCell(day)}
          isComplementaryCheckOut={isComplementaryCheckOutCell(day)}
          isIllegalBundleSelection={isIllegalBundleSelection(day)}
          complementaryPropertyName={complementaryBundleState?.offer?.property?.name}
          hasPromo={!!day.dealId}
          isTacticalPromotion={tacticalDays.includes(day.checkIn) && day.hotelAvailable}
          onClick={onDayClick}
          cheapestDay={cheapestDays[day.uniqueKey] ?? null}
          onInvalidBundleDateClick={onInvalidBundleDateClick}
          showUrgencyLabels={!isVillaOffer(offer)}
          displayMemberPrice={displayMemberPrice}
        />)}
      </Grid>
    </Root>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    offer: state.offer,
    bundleOfferId: state.bundleOffer?.id,
    currentOfferId: state.paginator?.currentOfferId,
    complementaryBundleState: getComplementaryBundleState(state),
    duration: state.duration,
    flights: state.flights,
    enableCalendarRecommendations: !state.bundleOffer ? shouldEnableCalendarRecommendations(appState, state.offer) : false,
    cheapestDays: getCalendarCheapestDays(currentState, appState),
    displayMemberPrice: currentState.displayMemberPrice,
  }
})(OfferCalendarSidebarDaysMonth)
